import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { AbstractControl, FormBuilder, Validators } from '@angular/forms';
import { forkJoin } from 'rxjs/observable/forkJoin';
import { of } from 'rxjs/observable/of';
import { catchError } from 'rxjs/operators';
import { ManufacturingOrder } from '../../../../core/models/manufacturing-order';
import { OptionInterface } from '../../../../core/models/option.interface';
import { UserRole } from '../../../../core/models/user-role';
import { UserRoleApiService } from '../../../../core/services/api/user-role-api.service';
import { find } from '../../../../core/utils/collection.utils';
import { buildOptions } from '../../../../core/utils/option.utils';
import { formChanged } from '../../../../core/validators/form-changed.validator';
import { AbstractEditionModalForm } from '../edition-modal/abstract-edition-modal-form';

@Component({
  selector: 'app-material-form',
  templateUrl: './material-form.component.html',
  styleUrls: ['./material-form.component.css'],
  providers: [
    {
      provide: AbstractEditionModalForm,
      useExisting: forwardRef(
        () => MaterialFormComponent
      ),
    },
  ],
})
export class MaterialFormComponent extends AbstractEditionModalForm<any> implements OnInit, OnChanges {
  form: AbstractControl;
  receiverOptions: OptionInterface[];
  selectedReciver: { hash: string, label: string, isRole: boolean, isUser: boolean }[] = [];
  @Input() usersRoles: UserRole[];
  @Input() manufacturingOrder: ManufacturingOrder;
  /**
   * When true the user does not pick recipients: the modal resolves them from
   * `recipientNames` (full names) via the existing /users-roles/search endpoint
   * and shows them read-only. Used by the BY_PART planner inline edit on the
   * material cell. The article viewer leaves this off and keeps the selector.
   */
  @Input() useRecipientsMatrix = false;
  @Input() recipientNames: string[] = [];
  @Output() searchReceiver = new EventEmitter<string>();

  constructor(
    private fb: FormBuilder,
    private userRoleApiService: UserRoleApiService
  ) {
    super();
  }

  ngOnInit() {
    this.initForm();
    if (this.useRecipientsMatrix) {
      this.resolveMatrixRecipients();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.hasOwnProperty('usersRoles')) {
      this.buildUsersRolesOptions();
    }
    if ((changes.hasOwnProperty('recipientNames') || changes.hasOwnProperty('useRecipientsMatrix'))
        && this.useRecipientsMatrix && this.form) {
      this.resolveMatrixRecipients();
    }
  }

  buildUsersRolesOptions() {
    if (this.selectedReciver && this.usersRoles) {
      const userRolesFiltered = this.usersRoles.filter(userRole => !this.selectedReciver.some(item => item.hash === userRole.hash));
      this.receiverOptions = buildOptions(userRolesFiltered, 'hash', 'name');
    }
  }

  onSelect(event) {
    if (!this.selectedReciver.some(item => item.hash === event.value)) {
      const userRole: UserRole = find(
        (ur: UserRole) => ur.hash === event.value,
        this.usersRoles
      );
      this.selectedReciver.push({
        hash: event.value,
        label: event.label,
        isRole: userRole ? userRole.isRole : null,
        isUser: userRole ? userRole.isUser : null
      });
      this.buildUsersRolesOptions();
    }
    this.syncHasReciverFlag();
    this.form.get('receiverHash').setValue('');
  }

  removeItem(item: any) {
    const index = this.selectedReciver.indexOf(item);
    if (index !== -1) {
      this.selectedReciver.splice(index, 1);
      this.buildUsersRolesOptions();
    }
    this.syncHasReciverFlag();
  }

  /**
   * Resolves each configured name via /users-roles/search (first token only,
   * exact full-name match in the client). Unmatched names are silently skipped.
   * Mirrors the delivery-date / quantity forms.
   */
  private resolveMatrixRecipients() {
    this.selectedReciver = [];
    if (!this.recipientNames || this.recipientNames.length === 0) {
      this.syncHasReciverFlag();
      return;
    }
    const searches = this.recipientNames.map(name => {
      const firstToken = (name || '').trim().split(/\s+/)[0];
      if (!firstToken) {
        return of([] as UserRole[]);
      }
      return this.userRoleApiService.searchUsersAndRoles(firstToken).pipe(catchError(() => of([])));
    });
    forkJoin(searches).subscribe(results => {
      this.recipientNames.forEach((name, idx) => {
        const matches = (results[idx] as UserRole[]) || [];
        const exact = matches.find(ur => ur.isUser && this.namesMatch(ur.name, name));
        if (exact && !this.selectedReciver.some(item => item.hash === exact.hash)) {
          this.selectedReciver.push({
            hash: exact.hash,
            label: exact.name,
            isRole: false,
            isUser: true,
          });
        }
      });
      this.syncHasReciverFlag();
    });
  }

  private namesMatch(a: string, b: string): boolean {
    return !!a && !!b && a.trim().toLowerCase() === b.trim().toLowerCase();
  }

  private syncHasReciverFlag() {
    if (!this.form) {
      return;
    }
    if (this.selectedReciver.length > 0) {
      this.form.get('hasReciver').setValue(true);
    } else {
      this.form.get('hasReciver').setValue(null);
    }
    this.form.updateValueAndValidity({ onlySelf: false, emitEvent: false });
  }

  initForm() {
    this.form = this.fb.group({
      hasReciver: [null, [Validators.required]],
      receiverHash: [''],
      material: [
        '',
        [
          Validators.required
        ],
      ],
      materialHash: [''],
    });
    this.updateForm();
  }

  updateForm(manufacturingOrderComment?: any, _params?: any) {
    const value = {
      receiverHash: manufacturingOrderComment
        ? manufacturingOrderComment.receiverHash
        : '',
      material: manufacturingOrderComment
        ? manufacturingOrderComment.material
        : this.manufacturingOrder.material.code,
      materialHash: manufacturingOrderComment
        ? manufacturingOrderComment.material
        : this.manufacturingOrder.material.hash,
    };
    this.resetForm(value);
  }

  resetForm(value) {
    this.form.setValidators([formChanged(value)]);
    this.form.reset(value);
  }

  getFormData() {
    const formValue = this.form.value;

    return {
      receivers: this.selectedReciver.map(reciver => {
        return {
          hash: reciver.hash,
          isRole: reciver.isRole,
          isUser: reciver.isUser
        };
      }),
      material: formValue.material,
      materialHash: formValue.materialHash,
    };
  }
}



// WEBPACK FOOTER //
// /var/www/borneo/prod/borneo_app/src/app/shared/components/modals/material-form/material-form.component.ts